import { useState } from "react";
import { format } from "date-fns";
import type { DateRange } from "react-day-picker";
import { usePagedOrders, useOrdersByDateRange } from "../hooks/useOrders";
import { CreateOrderDialog } from "../components/orders/CreateOrderDialog";
import { OrdersTable } from "../components/orders/OrdersTable";
import { DispatchReadinessPanel } from "../components/orders/DispatchReadinessPanel";
import { LoadingAnimation } from "@/shared/components/LoadingAnimation";
import { useAuthStore } from "@/features/auth/stores/authStore";
import { Button } from "@/components/ui/button";
import { Calendar } from "@/components/ui/calendar";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { CalendarRange, X } from "lucide-react";
import { cn } from "@/lib/utils";

export const OrderManagement = () => {
  const [search, setSearch] = useState("");
  const [pageNumber, setPageNumber] = useState(1);
  const [pageSize, setPageSize] = useState(20);
  const [dateRange, setDateRange] = useState<DateRange | undefined>(undefined);
  const [calendarOpen, setCalendarOpen] = useState(false);
  const [activeTab, setActiveTab] = useState("all-orders");
  const { hasPermission } = useAuthStore();
  const canCreate = hasPermission("orders.create");
  const canApproveDispatch = hasPermission("orders.approve_dispatch");

  const startDate = dateRange?.from ? format(dateRange.from, "yyyy-MM-dd") : undefined;
  const endDate = dateRange?.to ? format(dateRange.to, "yyyy-MM-dd") : undefined;
  const isDateFilterActive = !!startDate && !!endDate;

  const pagedQuery = usePagedOrders({ search: search || undefined, pageNumber, pageSize });
  const dateRangeQuery = useOrdersByDateRange(
    isDateFilterActive ? startDate : undefined,
    isDateFilterActive ? endDate : undefined
  );

  const activeQuery = isDateFilterActive ? dateRangeQuery : pagedQuery;
  const data = isDateFilterActive ? undefined : pagedQuery.data;
  const dateRangeOrders = isDateFilterActive ? (dateRangeQuery.data ?? []) : [];
  const isLoading = activeQuery.isLoading;
  const error = activeQuery.error;

  const handleSearch = (value: string) => {
    setSearch(value);
    setPageNumber(1);
  };

  const handlePageSizeChange = (size: number) => {
    setPageSize(size);
    setPageNumber(1);
  };

  const clearDateFilter = () => {
    setDateRange(undefined);
  };

  const dateRangeLabel = (() => {
    if (dateRange?.from && dateRange?.to)
      return `${format(dateRange.from, "MMM d, yyyy")} – ${format(dateRange.to, "MMM d, yyyy")}`;
    if (dateRange?.from)
      return `${format(dateRange.from, "MMM d, yyyy")} – pick end date`;
    return "Filter by date range";
  })();

  if (isLoading && !data && !dateRangeQuery.data) {
    return <LoadingAnimation fullScreen />;
  }

  if (error) {
    return (
      <div className="rounded-lg border border-red-200 bg-red-50 p-4">
        <p className="text-red-600">Failed to load orders</p>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="min-w-0">
          <h1 className="text-2xl font-bold tracking-tight sm:text-3xl">Orders</h1>
          <p className="text-gray-500 mt-1">
            Manage propagation orders from farmers
          </p>
        </div>
        {canCreate && activeTab === "all-orders" && <CreateOrderDialog />}
      </div>

      <Tabs value={activeTab} onValueChange={setActiveTab}>
        {canApproveDispatch && (
          <TabsList>
            <TabsTrigger value="all-orders">All Orders</TabsTrigger>
            <TabsTrigger value="dispatch-readiness">Dispatch Readiness</TabsTrigger>
          </TabsList>
        )}

        <TabsContent value="all-orders" className="space-y-6 mt-4">
          {/* Date Range Filter */}
          <div className="flex items-center gap-2">
            <Popover open={calendarOpen} onOpenChange={setCalendarOpen}>
              <PopoverTrigger asChild>
                <Button
                  variant="outline"
                  className={cn(
                    "justify-start gap-2 text-left font-normal min-w-[260px]",
                    !isDateFilterActive && "text-muted-foreground"
                  )}
                >
                  <CalendarRange className="h-4 w-4 shrink-0" />
                  <span>{dateRangeLabel}</span>
                </Button>
              </PopoverTrigger>
              <PopoverContent className="w-auto p-0" align="start">
                <Calendar
                  mode="range"
                  selected={dateRange}
                  onSelect={(range) => {
                    setDateRange(range);
                    // Only close once the user has picked two *different* dates
                    if (
                      range?.from &&
                      range?.to &&
                      range.from.toDateString() !== range.to.toDateString()
                    ) {
                      setCalendarOpen(false);
                    }
                  }}
                  numberOfMonths={2}
                  initialFocus
                />
              </PopoverContent>
            </Popover>

            {isDateFilterActive && (
              <Button
                variant="ghost"
                size="sm"
                onClick={clearDateFilter}
                className="gap-1 text-muted-foreground hover:text-foreground"
              >
                <X className="h-3.5 w-3.5" />
                Clear
              </Button>
            )}
          </div>

          <OrdersTable
            orders={isDateFilterActive ? dateRangeOrders : (data?.items ?? [])}
            search={search}
            onSearchChange={handleSearch}
            currentPage={isDateFilterActive ? 1 : (data?.currentPage ?? pageNumber)}
            pageSize={isDateFilterActive ? dateRangeOrders.length || 20 : (data?.pageSize ?? pageSize)}
            totalCount={isDateFilterActive ? dateRangeOrders.length : (data?.totalCount ?? 0)}
            totalPages={isDateFilterActive ? 1 : (data?.totalPages ?? 1)}
            onPageChange={setPageNumber}
            onPageSizeChange={handlePageSizeChange}
          />
        </TabsContent>

        {canApproveDispatch && (
          <TabsContent value="dispatch-readiness" className="mt-4">
            <DispatchReadinessPanel active={activeTab === "dispatch-readiness"} />
          </TabsContent>
        )}
      </Tabs>
    </div>
  );
};